// The desktop sidebar is either full (240px) or an icon rail (64px), a
// per-device choice kept in localStorage like the theme. Until someone
// chooses, a narrow desktop window (landscape tablets, small laptops) gets the
// rail. <html data-sidebar> carries the result so the loading skeleton can
// size itself in CSS (the `rail:` variant in packages/ui/src/styles.css).
import { useSyncExternalStore } from 'react'

type SidebarChoice = 'full' | 'rail' | null

const KEY = 'ims.sidebar'
const listeners = new Set<() => void>()
const narrow = typeof window === 'undefined' ? null : window.matchMedia('(max-width: 1279px)')

function read(): SidebarChoice {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'full' || value === 'rail' ? value : null
  } catch {
    return null
  }
}

let choice: SidebarChoice = read()

function collapsed(): boolean {
  return choice ? choice === 'rail' : (narrow?.matches ?? false)
}

function apply() {
  document.documentElement.dataset.sidebar = collapsed() ? 'rail' : 'full'
  for (const listener of listeners) listener()
}

/** Applies the saved choice. Call once before the first render. */
export function initSidebar() {
  apply()
  narrow?.addEventListener('change', apply)
}

export function toggleSidebar() {
  choice = collapsed() ? 'full' : 'rail'
  try {
    localStorage.setItem(KEY, choice)
  } catch {
    // Storage blocked: the choice still applies for this session.
  }
  apply()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** True when the sidebar is the icon rail. */
export function useSidebarCollapsed(): boolean {
  return useSyncExternalStore(subscribe, collapsed)
}
